/**
 * Per-check editor (#86, simplified). ONE "Edit" button per C1–C5 card opens this
 * modal, which edits exactly two things:
 *   - the summary/explanation text (user_overrides.user_rationale)
 *   - the decision (Accept / Decline-branch), editable before AND after a decision.
 *
 * The AI-extracted-field form was dropped (#86): the top explanation is all the
 * workpaper needs, and routing (C2 status / C5 opinion) is corrected by changing
 * the decision, not by editing fields. C6 has no section-level Edit — it's decided
 * per subservice-org card (see #99).
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Modal,
  Select,
  SpaceBetween,
  Textarea,
  type SelectProps,
} from "@cloudscape-design/components";
import type { ScopeCheck, UpdateScopeCheckRequest } from "@/core/api/types";
import { updateScopeCheck } from "./api";

type Overrides = Record<string, unknown>;

interface Props {
  check: ScopeCheck;
  visible: boolean;
  onDismiss: () => void;
  onSaved: () => void;
}

/** The decision choices for a check — the *answer* to its question (V3 Section
 *  1). The `value` is the frontend↔backend contract that routes to action items
 *  (see actions.py), the audit log, and the workpaper. `reason: true` surfaces
 *  the optional note field. `setsFlag` marks a guided pseudo-option that writes
 *  a user_overrides flag instead of a user_decision (C5's "Modified" step — only
 *  used by the inline buttons, not this modal). C6 is decided per carve-out. */
export interface DecisionOpt {
  value: string;
  label: string;
  reason?: boolean;
  setsFlag?: string;
}
export function decisionOptionsFor(check: ScopeCheck): DecisionOpt[] {
  // #119: tailored per-check answers. C5 is the flat terminal list here (the
  // Edit modal); the inline card buttons present it as a guided two-step.
  switch (check.check_num) {
    case 1:
      return [
        { value: "correct", label: "Correct scope" },
        { value: "incorrect", label: "Incorrect scope — get new SOC 1", reason: true },
      ];
    case 2:
      return [
        { value: "full", label: "Full — covers the fiscal year" },
        { value: "partial", label: "Partial — bridge letter" },
        { value: "wrong_period", label: "Wrong year — get new SOC 1 (gap too large for a bridge)", reason: true },
        { value: "none", label: "No overlap — get new SOC 1", reason: true },
      ];
    case 3:
      return [
        { value: "type_ii", label: "SOC 1 Type II" },
        { value: "not", label: "Not Type II (SOC 2 / Type I) — get new SOC 1", reason: true },
      ];
    case 4:
      return [
        { value: "acceptable", label: "Acceptable — auditor is reputable" },
        { value: "reject", label: "Reject — get new SOC 1", reason: true },
      ];
    case 5:
      return [
        { value: "unmodified", label: "Unmodified" },
        { value: "rely", label: "Modified — rely (document impact)", reason: true },
        { value: "non_reliance", label: "Modified — non-reliance", reason: true },
      ];
    default:
      return []; // C6 — decided per carve-out
  }
}

export function EditCheckModal({ check, visible, onDismiss, onSaved }: Props) {
  const overrides = (check.user_overrides ?? {}) as Overrides;
  const aiRationale = check.ai_rationale ?? "";
  const currentRationale = (overrides.user_rationale as string | undefined) ?? aiRationale;
  const currentDecision = check.user_decision ?? "";
  const decisionOpts = decisionOptionsFor(check);
  // #119: the Edit modal now only opens on a Done check — Open checks decide via
  // the inline buttons on the card. So default to the human's decision if set,
  // else the AI's recommended option, which matches the header verdict (an
  // AI-accepted C2-partial reads "Partial — accept with a bridge letter", not
  // "Not yet decided"). That option stays selectable to reopen a Done check.
  const defaultDecision = currentDecision || decisionOpts[0]?.value || "";

  const [rationale, setRationale] = useState(currentRationale);
  const [decision, setDecision] = useState(defaultDecision);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setRationale(currentRationale);
      setDecision(defaultDecision);
      setReason("");
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, check.id]);

  const selectedDecisionOpt = decisionOpts.find((o) => o.value === decision);
  // #119: lead with "Not yet decided" (value "") so an Open check can show its
  // true undecided state — and a decided check can be reverted to it.
  const decisionSelectOpts: SelectProps.Option[] = [
    { label: "Not yet decided", value: "" },
    ...decisionOpts.map((o) => ({ label: o.label, value: o.value })),
  ];

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const body: UpdateScopeCheckRequest = {};
      // "Not yet decided" (empty value) on a decided check is a reopen.
      const reopening = decision !== currentDecision && decision === "";
      if (rationale !== currentRationale || reopening) {
        const ov: Overrides = { ...overrides };
        if (rationale !== currentRationale) ov.user_rationale = rationale;
        // #119: reopening clears C5's two-step flag so it returns to step 1.
        if (reopening) delete ov.c5_modified;
        body.user_overrides = ov;
      }
      if (decision !== currentDecision) {
        body.user_decision = decision; // "" clears the decision (reopen)
        if (selectedDecisionOpt?.reason) body.decline_reason = reason || null;
      }
      await updateScopeCheck(check.id, body);
      onSaved();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Edit C${check.check_num}`}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onDismiss} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={save} disabled={busy} loading={busy}>
              Save
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Save failed">
            {error}
          </Alert>
        )}

        <FormField label="Explanation" description="The reasoning shown on the card. Edit the AI's draft.">
          <Textarea value={rationale} onChange={({ detail }) => setRationale(detail.value)} rows={5} />
        </FormField>

        {decisionOpts.length > 0 && (
          <FormField
            label="Decision"
            description="Accept the check or route it to an action item. You can change this any time."
          >
            <SpaceBetween size="xs">
              <Select
                selectedOption={decisionSelectOpts.find((o) => o.value === decision) ?? decisionSelectOpts[0]!}
                onChange={({ detail }) => setDecision(detail.selectedOption.value ?? "")}
                options={decisionSelectOpts}
              />
              {selectedDecisionOpt?.reason && (
                <Textarea
                  value={reason}
                  onChange={({ detail }) => setReason(detail.value)}
                  rows={3}
                  placeholder="Optional note for the action item — what triggered this decision."
                />
              )}
            </SpaceBetween>
          </FormField>
        )}
      </SpaceBetween>
    </Modal>
  );
}
